import { useFormikContext } from "formik";
import * as Yup from "yup";
import {
  FormControlLabel,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
} from "@mui/material";
import FeatureLock from "@/components/FeatureLock";
import useOrganizationPlan from "@/hooks/useOrganizationPlan";
import { Content } from "@/types";
import { HugeiconsIcon } from "@hugeicons/react";
import { GiftIcon, Money01Icon, PercentIcon } from "@hugeicons/core-free-icons";

export const LoyaltySchema = Yup.object().shape({
  loyalty: Yup.object().shape({
    isRedeemable: Yup.boolean(),
    pointsCost: Yup.number().when("isRedeemable", {
      is: true,
      then: (schema) => schema.required("Campo requerido").min(1, "Debe ser al menos 1"),
    }),
    rewardType: Yup.string().oneOf(["item", "discount_pct", "discount_fixed"]),
    rewardValue: Yup.number().when("rewardType", {
      is: (val: string) => val === "discount_pct" || val === "discount_fixed",
      then: (schema) => schema.required("Campo requerido").min(1, "Debe ser al menos 1"),
    }),
    hideFromMenu: Yup.boolean(),
  }),
});

const LoyaltyTab = () => {
  const { isFeatureAvailable } = useOrganizationPlan("loyalty");
  const { values, handleChange, errors, touched, handleBlur, setFieldValue } = useFormikContext<Content>();

  const handleLoyaltyChange = (field: string, value: any) => {
    setFieldValue(`loyalty.${field}`, value);
  };

  if (!isFeatureAvailable("rewards")) {
    return (
      <FeatureLock
        icon={GiftIcon}
        title="Premia la lealtad de tus clientes"
        description="Con el plan Pro, puedes activar un programa de puntos automatizado. Tus clientes acumularán puntos con cada compra que podrán canjear por productos o descuentos que tú definas. ¡Fideliza a tu comunidad hoy mismo!"
      />
    );
  }

  return (
    <Stack gap={2}>
      <Paper sx={{ padding: 2 }} elevation={2}>
        <Stack gap={2}>
          <Typography variant="h4">Canje de Puntos</Typography>

          <FormControlLabel
            label="Habilitar como Recompensa"
            labelPlacement="start"
            sx={{
              margin: 0,
              display: "flex",
              justifyContent: "space-between",
            }}
            control={
              <Switch
                checked={Boolean(values?.loyalty?.isRedeemable)}
                onChange={(e) => {
                  handleLoyaltyChange("isRedeemable", e.target.checked);
                  handleLoyaltyChange("pointsCost", 1);
                  handleLoyaltyChange("rewardType", 'item');
                  handleLoyaltyChange("hideFromMenu", false);
                }}
              />
            }
          />

          {values?.loyalty?.isRedeemable && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                label="Costo en Puntos"
                name="loyalty.pointsCost"
                type="number"
                value={values?.loyalty?.pointsCost ?? ""}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!(touched.loyalty as any)?.pointsCost && !!(errors.loyalty as any)?.pointsCost}
                helperText={(touched.loyalty as any)?.pointsCost && (errors.loyalty as any)?.pointsCost}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">pts</InputAdornment>,
                  },
                }}
              />

              <Stack spacing={1}>
                <Typography variant="body1" color="text.secondary">
                  Tipo de Recompensa
                </Typography>
                <ToggleButtonGroup
                  color="primary"
                  fullWidth
                  exclusive
                  size="small"
                  value={values?.loyalty?.rewardType || "item"}
                  onChange={(_e, value) => value && handleLoyaltyChange("rewardType", value)}
                >
                  <ToggleButton value="item">
                    <Stack alignItems="center" py={1}>
                      <HugeiconsIcon icon={GiftIcon} size={24} />
                      <Typography variant="caption" sx={{ mt: 0.5 }}>Producto</Typography>
                    </Stack>
                  </ToggleButton>
                  <ToggleButton value="discount_pct">
                    <Stack alignItems="center" py={1}>
                      <HugeiconsIcon icon={PercentIcon} size={24} />
                      <Typography variant="caption" sx={{ mt: 0.5 }}>Descuento</Typography>
                    </Stack>
                  </ToggleButton>
                  <ToggleButton value="discount_fixed">
                    <Stack alignItems="center" py={1}>
                      <HugeiconsIcon icon={Money01Icon} size={24} />
                      <Typography variant="caption" sx={{ mt: 0.5 }}>Monto Fijo</Typography>
                    </Stack>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              {(values?.loyalty?.rewardType === "discount_pct" || values?.loyalty?.rewardType === "discount_fixed") && (
                <TextField
                  fullWidth
                  label={values?.loyalty?.rewardType === "discount_pct" ? "Porcentaje de Descuento" : "Monto de Descuento"}
                  name="loyalty.rewardValue"
                  type="number"
                  value={values?.loyalty?.rewardValue ?? ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    Boolean((values?.loyalty?.rewardType === "discount_pct" || values?.loyalty?.rewardType === "discount_fixed") && touched?.loyalty?.rewardValue && errors?.loyalty?.rewardValue)
                  }
                  helperText={(values?.loyalty?.rewardType === "discount_pct" || values?.loyalty?.rewardType === "discount_fixed") && touched?.loyalty?.rewardValue && errors?.loyalty?.rewardValue ? errors.loyalty?.rewardValue : ""}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          {values?.loyalty?.rewardType === "discount_pct" ? "%" : "C$"}
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            </Stack>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ padding: 2, mt: 2 }} elevation={2}>
        <Stack gap={2}>
          <Typography variant="h4">Visibilidad</Typography>
          <FormControlLabel
            label="Ocultar del Menú Público"
            labelPlacement="start"
            sx={{
              margin: 0,
              display: "flex",
              justifyContent: "space-between",
            }}
            control={
              <Switch
                checked={Boolean(values?.loyalty?.hideFromMenu)}
                onChange={(e) => handleLoyaltyChange("hideFromMenu", e.target.checked)}
              />
            }
          />
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Ideal para productos que solo se pueden obtener canjeando puntos. Si se activa, este producto no aparecerá en la carta normal, solo será visible en el flujo de canje.
          </Alert>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default LoyaltyTab;
